import { Box, Divider, IconButton, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { TMassPostSaleJobOrder } from 'redux/reducers/massPostSaleJobs/types';
import {
	clearMassPostSaleJobOrders,
	getMassPostSaleJobOrdersRequest,
} from 'redux/reducers/massPostSaleJobs/reducer';
import {
	getMassPostSaleJobOrders,
	getMassPostSaleJobOrdersLoading,
	getMassPostSaleJobs,
} from 'redux/reducers/massPostSaleJobs/selectors';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import { tableStructure } from './tableStructure';
import { MASS_POST_SALE_JOB_ORDER_PROCESSING_TYPE_FILTER } from '../constants';

/** Scope of the page — always taken from the route, never from the filter panel. */
const SCOPE_FIELDS = ['jobId', 'processingType'];

export const MassPostSaleJobOrders: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const { jobId, processingType } = useParams<{ jobId: string; processingType: string }>();

	const savedUrl = useSelector(getSavedUrl);
	const orders = useSelector(getMassPostSaleJobOrders);
	const ordersLoading = useSelector(getMassPostSaleJobOrdersLoading);
	const jobs = useSelector(getMassPostSaleJobs);

	// Available when the page was opened from the jobs list — used for the header only.
	const job = useMemo(
		() =>
			jobs?.data?.find(
				(item) =>
					String(item.id) === String(jobId) &&
					String(item.processingType) === String(processingType),
			) ?? null,
		[jobs, jobId, processingType],
	);

	const processingTypeLabel =
		MASS_POST_SALE_JOB_ORDER_PROCESSING_TYPE_FILTER.find(
			(option) => String(option[0]) === String(processingType),
		)?.[1] ?? processingType;

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TMassPostSaleJobOrder>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	useEffect(() => {
		if (!jobId || !processingType) return;
		const params = queryString.parse(search);
		const filters = (getFiltersFromNavigateParams(tableStructure, params) as TFilters).filter(
			(filter) => !SCOPE_FIELDS.includes(filter.fieldName),
		);

		filters.push({
			fieldName: 'jobId',
			value: jobId,
			operator: '=',
		});
		filters.push({
			fieldName: 'processingType',
			value: processingType,
			operator: '=',
		});

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: tableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getMassPostSaleJobOrdersRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [dispatch, search, jobId, processingType]);

	useEffect(() => {
		return () => {
			dispatch(clearMassPostSaleJobOrders());
		};
	}, [dispatch]);

	const handleBack = useCallback(() => {
		if (savedUrl?.includes(PATHS.MASS_POST_SALE_JOBS_PAGE)) navigate(savedUrl);
		else navigate(PATHS.MASS_POST_SALE_JOBS_PAGE);
	}, [navigate, savedUrl]);

	const handleAction = useCallback(() => undefined, []);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TMassPostSaleJobOrder>
				tableStructure={currentTableStructure}
				items={orders}
				itemsIsLoading={ordersLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'start',
						alignItems: 'center',
						gap: '10px',
					}}
				>
					<IconButton color="primary" sx={{ p: '0' }} onClick={handleBack}>
						<KeyboardBackspace />
					</IconButton>
					<Typography variant="body1" color="primary" fontWeight="bold">
						{`Mass Post Sale Job #${jobId ?? ''}`}
					</Typography>
					<Divider orientation="vertical" flexItem color="primary.main" />
					<Typography variant="body1" color="primary" fontWeight="bold">
						{processingTypeLabel ?? ''}
					</Typography>
					{!!job?.emFullName && (
						<>
							<Divider orientation="vertical" flexItem color="primary.main" />
							<Typography variant="body1" color="primary" fontWeight="bold">
								{job.emFullName}
							</Typography>
						</>
					)}
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
